import { Button, Div } from '@codesuma/baseline'
import { IDayCell } from '../../../services/calendar'
import { CalendarSystem } from '../../../services/settings'
import occasions from '../../../services/occasions'
import customOccasions from '../../../services/custom-occasions'
import countryOccasions from '../../../services/country-occasions'
import overrides from '../../../services/occasion-overrides'
import dayEvents from '../../../services/day-events'
import { eventTime, fromPhone } from '../../../services/phone-events'
import { Source, isPrimary } from '../../../services/day-holidays'
import { toFaDigits } from '../../../helpers/digits'
import { PhoneMark } from '../../../components/phone-mark'
import { Section } from './section'
import style from './index.module.css'

// Material Design "add" icon (Apache 2.0), inherits currentColor.
const PLUS_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z"/></svg>'

// Everything the month itself holds, day by day: what the user has on and what
// the day is named after, on one list rather than two. Adjacent-month cells are
// left out, day numbers are in the primary system and holidays are tinted. Any
// country the user added is in here too, each row saying whose day it is. The
// header and the scrolling are the Section's — see there.
//
// An event row is the one that ends in an hour, so the two kinds stay apart to
// read without a marker of their own. Under the lot is the way in to one more
// of either, which emits 'add' for the page to answer. Events come from whatever the service is
// holding at the time, Emrouz's own and the phone's together — a month whose
// phone events have not arrived yet is built again once they land, which is
// what the home page's 'phone-events' redraw is for.
export const MonthList = (system: CalendarSystem, cells: IDayCell[], scroller: HTMLElement) => {
    const base = Section('همه رویدادهای ماه', scroller)
    // The Gregorian slide is LTR for its grid, but this is Persian prose under
    // a Persian heading, so the section as a whole keeps reading right to left.
    // The rows are the exception — see below.
    if (system === 'gregorian') base.attr('dir', 'rtl')

    // Someone reading the Gregorian grid is living by the country they added,
    // so its day leads and Iran's follows. The other way round on the Jalaali
    // grid. The same rule the day page uses — see day-slide/occasions.ts.
    const foreignFirst = system === 'gregorian'

    // `mine` is one the user put there, an occasion of their own or an event.
    // It is never a holiday, so the two flags never meet — but they mark the row
    // differently, so both are asked for by name.
    // `original` is the name the country writes, above the Persian for it, and
    // `from` names the country. Iran's own rows carry neither: on a Persian
    // calendar that is the one that needs no saying. `time` is what makes a row
    // an event; nothing the calendar itself knows about a day has an hour.
    // `phone` is an event from the phone's own calendar, which carries a small
    // phone after its name.
    const row = (c: IDayCell, title: string,
        { isHoliday = false, mine = false, original = '', from = '', source = 'iran', time = '', phone = false }:
            { isHoliday?: boolean, mine?: boolean, original?: string, from?: string, source?: Source, time?: string,
                phone?: boolean }) => {
        const line = Div()
        line.addClass(style.occasion)
        // The holiday colour belongs to whichever calendar is being read; the
        // other side takes the second colour. One rule for every surface — see
        // services/day-holidays.ts.
        if (isHoliday) {
            line.addClass(isPrimary(source, system)
                ? style.occasionHoliday
                : style.occasionHolidayOther)
        }
        if (mine) line.addClass(style.occasionMine)
        // The day number leads, so on the Gregorian grid it leads from the left
        // like every other number on that slide. Only the row turns: the
        // heading above stays where a Persian heading belongs.
        if (system === 'gregorian') line.attr('dir', 'ltr')

        const day = Div(system === 'gregorian' ? String(c.gd) : toFaDigits(c.jd))
        day.addClass(style.occasionDay)
        line.append(day)

        const text = Div()
        text.addClass(style.occasionText)

        // Each line decides its own direction: Latin reads left to right and
        // Persian right to left, whichever way the row around them is running.
        if (original && original !== title) {
            const first = Div(original)
            first.addClass(style.occasionOriginal)
            first.attr('dir', 'auto')
            text.append(first)
        }

        const main = Div(title)
        main.attr('dir', 'auto')
        if (phone) main.append(PhoneMark())
        text.append(main)

        if (from) {
            const country = Div(from)
            country.addClass(style.occasionFrom)
            country.attr('dir', 'auto')
            text.append(country)
        }
        line.append(text)

        if (time) {
            const at = Div(time)
            at.addClass(style.eventTime)
            line.append(at)
        }

        base.list.append(line)
    }

    // Both lists as the reader has left them on the day page: their own names,
    // and without the ones they put away. See services/occasion-overrides.
    const iranian = (c: IDayCell) => {
        for (const o of overrides.builtIns(occasions.forDay(c)).shown) row(c, o.title, { isHoliday: o.isHoliday })
    }

    const foreign = (c: IDayCell) => {
        for (const o of overrides.countries(countryOccasions.forDay(c)).shown) {
            row(c, o.title, {
                isHoliday: o.type === 'public_holiday',
                source: 'abroad',
                original: o.names.local ?? o.names.en,
                from: o.isObserved ? `${o.countryName} — روز تعطیلی رسمی` : o.countryName,
            })
        }
    }

    for (const c of cells) {
        if (!c.inMonth) continue
        // What the user has on leads the day: an appointment is theirs, and the
        // name a day carries belongs to everyone.
        for (const e of dayEvents.forDay(c)) row(c, e.title, { mine: true, time: eventTime(e), phone: fromPhone(e) })
        if (foreignFirst) {
            foreign(c)
            iranian(c)
        } else {
            iranian(c)
            foreign(c)
        }
        for (const o of customOccasions.forDay(c)) row(c, o.title, { mine: true })
    }

    // One more line under the rows, the way in to something the month does not
    // hold yet. One offer for both kinds: what to make is the first thing the
    // page asks after the tap.
    const add = Button()
    add.addClass(style.addRow)
    add.html(`${PLUS_SVG}<span>افزودن رویدادی دیگر</span>`)
    add.on('click', () => base.emit('add'))
    base.list.append(add)

    return base
}
